import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/Help';
import {
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { PropsWithChildren } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { ITheme } from '../../../../theme';
import ProgressSteps from '../../../molecules/ProgressSteps/ProgressSteps';

const useStyles = makeStyles()((theme: ITheme) => ({
  seperator: {
    height: '1px',
    width: '100%',
    background: '#d1cfcf',
    margin: '1em 0',
  },
  header: {
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    gap: 5,
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
      gap: 2,
    },
  },
  back: {
    position: 'absolute',
    top: 20,
    left: 0,
    color: '#fff',
    [theme.breakpoints.down('sm')]: {
      top: 10,
    },
  },
  tooltipIcon: {
    color: '#ffffff78',
  },
  content: {
    overflowY: 'auto',
    overflowX: 'clip',
    flexGrow: 1,
    width: '100%',
    maxHeight: '70vh',
  },
  spinner: {
    textAlign: 'center',
    height: '6em',
  },
  close: {
    position: 'absolute',
    right: '1em',
    top: '.8em',
    [theme.breakpoints.down('sm')]: {
      right: '.5em',
      top: '.3em',
    },
  },
  dialogButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1em',
    padding: '1em',
  },
  placeholder: {
    height: '8px',
  },
}));

export interface TranscriptionDialogWrapperProps extends PropsWithChildren {
  handleClose: () => void;
  currentStep?: number;
  title?: string;
  helperText?: string;
  numberOfSteps?: number;
  handleBackClick?: () => void;
  submitting: boolean;
}

const TranscriptionDialogWrapper: React.FC<TranscriptionDialogWrapperProps> = ({
  handleClose,
  currentStep,
  title,
  helperText,
  numberOfSteps,
  children,
  handleBackClick,
  submitting,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('sm'));

  const onClose = () => {
    handleClose();
  };
  return (
    <>
      {numberOfSteps && <ProgressSteps currentStep={currentStep} numberOfSteps={numberOfSteps} />}
      <div className={classes.placeholder} />
      <IconButton
        aria-label="Close transcription dialog"
        onClick={onClose}
        data-testid="wizard_close"
        id={`btn_wizard_close_${currentStep}`}
        className={classes.close}>
        <CloseIcon />
      </IconButton>
      <Typography className={classes.header} variant="h6">
        {title}
        <Tooltip title={helperText} arrow placement="top">
          <HelpIcon className={classes.tooltipIcon} />
        </Tooltip>
      </Typography>

      <div className={classes.seperator} />
      <div className={classes.content}>
        {submitting ? (
          <div className={classes.spinner}>
            <CircularProgress size={50} color="primary" />
          </div>
        ) : (
          children
        )}
      </div>
      {currentStep > 0 && (
        <Button
          id={`btn_wizard_back_${currentStep}`}
          className={classes.back}
          onClick={handleBackClick}
          color="primary">
          <ArrowBackIcon />
          {!isMobile && t('home.input.dialog.back')}
        </Button>
      )}
    </>
  );
};

export default TranscriptionDialogWrapper;
